import { useEffect, useState } from 'react';
import { Layout } from '@/components/common/Layout/Layout';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/lib/store';
import { ROUTES } from '@/constants/routes';
import api from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';

// Import our new components
import { DailyTimeEntry } from './DailyTimeEntry';
import { DashboardSettingsProvider, useDashboardSettings } from './DashboardSettingsContext';
// Import the new statistics components
import { DashboardStatistics, FirmInsightsPanel, FirmStatistics, TeamStatistics } from '@/components/dashboard';
import { 
  TimeEntryRow 
} from './types';

import { formatDate } from './utils';
import { isSuperUser as checkSuperUser, isAdmin as checkAdmin, isManager as checkManager } from '@/lib/permissions';

// Component that handles the main dashboard logic and data fetching
function DashboardContent() {
  const { user } = useAuth();
  const userGroups = user?.groups || [];
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { matters, fetchAll, isLoading: storeLoading } = useStore();
  const { settings } = useDashboardSettings();
  
  // User role and permission flags
  const isSuperUser = checkSuperUser(userGroups);
  const isAdmin = checkAdmin(userGroups);
  const isManager = checkManager(userGroups);
  const canViewAdminStats = isSuperUser || isAdmin || isManager;

  // State variables
  const [activeTimer, setActiveTimer] = useState<number | null>(null);
  const [timeEntries, setTimeEntries] = useState<TimeEntryRow[]>([]);

  
  // Statistics state
  const [dayStats, setDayStats] = useState<any>(null);
  const [monthStats, setMonthStats] = useState<any>(null);
  const [yearStats, setYearStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      
      // Pass the selected date as reference_date so stats match the timesheet day
      const refDate = format(selectedDate, 'yyyy-MM-dd');
      const fetchPromises = [
        api.get(`${ROUTES.API.STATISTICS}users/my_stats/?period=day&reference_date=${refDate}`),
        api.get(`${ROUTES.API.STATISTICS}users/my_stats/?period=month&reference_date=${refDate}`),
        api.get(`${ROUTES.API.STATISTICS}users/my_stats/?period=year&reference_date=${refDate}`)
      ];

      const responses = await Promise.all(fetchPromises);
      
      setDayStats(responses[0].data);
      setMonthStats(responses[1].data);
      setYearStats(responses[2].data);
      
      setStatsLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard statistics:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch dashboard statistics',
        variant: 'destructive',
      });
      setStatsLoading(false);
    }
  };

  // Fetch time entries
  const fetchTimeEntries = async () => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', String(user?.id));
      
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      queryParams.append('start_date', formattedDate);
      queryParams.append('end_date', formattedDate);
      
      const response = await api.get(`${ROUTES.API.TIMESHEET}query/?${queryParams.toString()}`);
      const entries = response.data.results || [];
      
      const processedEntries = entries.map((entry: any) => ({
        id: entry.id,
        date: entry.date,
        matter: entry.matter,
        description: entry.description,
        hours: entry.hours.toString(),
        billable: entry.billable,
        timer_active: entry.timer_active,
        matterName: matters.find((m: any) => m.id === entry.matter)?.name || "Unknown Matter",
        user: entry.user,
        isNew: false,
        originalValues: {
          matter: entry.matter,
          description: entry.description,
          hours: entry.hours.toString(),
          billable: entry.billable
        }
      }));
      
      processedEntries.push({ 
        matter: 0, 
        description: '', 
        hours: '', 
        billable: settings.defaultBillable, 
        isNew: true, 
        isEditing: true,
        originalValues: {
          matter: 0,
          description: '',
          hours: '',
          billable: settings.defaultBillable
        }
      });
      
      setTimeEntries(processedEntries);
      
      const activeEntries = entries.filter((entry: any) => entry.timer_active);
      setActiveTimer(activeEntries.length > 0 ? activeEntries[0].id : null);
    } catch (error) {
      console.error('Error fetching time entries:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch time entries',
        variant: 'destructive',
      });
    }
  };

  // Initialize data when component mounts
  useEffect(() => {
    fetchAll();
    fetchDashboardStats();
  }, []);
  
  // Fetch time entries when matters and clients are loaded
  useEffect(() => {
    if (!storeLoading && matters.length > 0) {
      fetchTimeEntries();
      fetchDashboardStats();
    }
  }, [selectedDate, storeLoading, matters.length]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <div className="text-sm text-muted-foreground">
            {formatDate(new Date(), settings)}
          </div>
        </div>

        {/* Daily Time Entry Component */}
        <DailyTimeEntry
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          timeEntries={timeEntries}
          setTimeEntries={setTimeEntries}
          activeTimer={activeTimer}
          setActiveTimer={setActiveTimer}
          onRefreshStats={() => fetchDashboardStats()}
          onRefreshTimeEntries={fetchTimeEntries}
          settings={settings}
        />

        {/* Key Statistics Overview */}
        <DashboardStatistics
          dayStats={dayStats}
          monthStats={monthStats}
          yearStats={yearStats}
          isLoading={statsLoading}
          settings={settings}
          selectedDate={selectedDate}
        />

        {/* Firm Insights (realization, collection, utilization, ageing, DSO).
            Replaces the confusing numbers previously shown — data comes from
            the reworked firm-stats service. */}
        {canViewAdminStats && <FirmInsightsPanel currency={settings.currency} />}

        {/* Detailed Firm Statistics for Admin Users */}
        {canViewAdminStats && (
          <FirmStatistics
            settings={settings}
          />
        )}

        {/* Team Statistics for Admin Users */}
        {canViewAdminStats && settings.showTeamStats && (
          <TeamStatistics
            settings={settings}
          />
        )}

      </div>
    </Layout>
  );
}

// Main Dashboard component with settings provider
export function Dashboard() {
  // Extract client ID from user for multi-tenant settings (adjust based on your user structure)
  const clientId = 'default'; // You can customize this based on your user structure
  
  return (
    <DashboardSettingsProvider clientId={clientId}>
      <DashboardContent />
    </DashboardSettingsProvider>
  );
}
